import { httpGet, httpPost, httpPut, httpPatch } from '../lib/http';

/**
 * Paper Attempt Service
 * Manages paper attempts with pause/resume/cancel functionality
 * Tracks user progress, answers, and time spent
 */

// Helper for logging
function logApiCall(name, params) {
  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    console.info(`[PaperAttempt] ${name}`, params);
  }
}

function logApiError(name, params, error) {
  console.error(`[PaperAttempt Error] ${name}`, params, error);
}

async function safeApiCall(fn, name, params) {
  logApiCall(name, params);
  try {
    const result = await fn();
    return result;
  } catch (error) {
    logApiError(name, params, error);
    return { success: false, error: error?.message || 'Unknown error' };
  }
}

/**
 * Start a new paper attempt
 * @param {string} paperId - Paper ID
 * @param {string} examId - Exam ID
 * @returns {Promise<Object>} - Attempt object with attemptId
 */
export const startPaperAttempt = async (paperId, examId) => safeApiCall(async () => {
  const res = await httpPost('/api/indian-exams/attempts', {
    paperId,
    examId,
    status: 'in_progress',
    startedAt: new Date().toISOString()
  });
  return await res.json();
}, 'startPaperAttempt', { paperId, examId });

/**
 * Get attempt details by ID
 * @param {string} attemptId - Attempt ID
 * @returns {Promise<Object>}
 */
export const getAttemptById = async (attemptId) => safeApiCall(async () => {
  const res = await httpGet(`/api/indian-exams/attempts/${encodeURIComponent(attemptId)}`);
  return await res.json();
}, 'getAttemptById', { attemptId });

/**
 * Save answer for a question in an attempt
 * @param {string} attemptId - Attempt ID
 * @param {string} questionId - Question ID
 * @param {Object} answerData - { selectedOption, timeSpent, isMarkedForReview }
 * @returns {Promise<Object>}
 */
export const saveAnswer = async (attemptId, questionId, answerData) => safeApiCall(async () => {
  const res = await httpPatch(`/api/indian-exams/attempts/${encodeURIComponent(attemptId)}/answers`, {
    questionId,
    ...answerData,
    timestamp: new Date().toISOString()
  });
  return await res.json();
}, 'saveAnswer', { attemptId, questionId });

/**
 * Update attempt progress (for auto-save)
 * @param {string} attemptId - Attempt ID
 * @param {Object} progressData - { currentQuestionIndex, answers, timeElapsed, status }
 * @returns {Promise<Object>}
 */
export const updateAttemptProgress = async (attemptId, progressData) => safeApiCall(async () => {
  const res = await httpPatch(`/api/indian-exams/attempts/${encodeURIComponent(attemptId)}/progress`, {
    ...progressData,
    lastUpdated: new Date().toISOString()
  });
  return await res.json();
}, 'updateAttemptProgress', { attemptId });

/**
 * Pause an attempt
 * @param {string} attemptId - Attempt ID
 * @param {Object} currentState - { currentQuestionIndex, answers, timeElapsed }
 * @returns {Promise<Object>}
 */
export const pauseAttempt = async (attemptId, currentState) => safeApiCall(async () => {
  const res = await httpPatch(`/api/indian-exams/attempts/${encodeURIComponent(attemptId)}/pause`, {
    status: 'paused',
    pausedAt: new Date().toISOString(),
    ...currentState
  });
  return await res.json();
}, 'pauseAttempt', { attemptId });

/**
 * Resume a paused attempt
 * @param {string} attemptId - Attempt ID
 * @returns {Promise<Object>}
 */
export const resumeAttempt = async (attemptId) => safeApiCall(async () => {
  const res = await httpPatch(`/api/indian-exams/attempts/${encodeURIComponent(attemptId)}/resume`, {
    status: 'in_progress',
    resumedAt: new Date().toISOString()
  });
  return await res.json();
}, 'resumeAttempt', { attemptId });

/**
 * Cancel an attempt
 * @param {string} attemptId - Attempt ID
 * @returns {Promise<Object>}
 */
export const cancelAttempt = async (attemptId) => safeApiCall(async () => {
  const res = await httpPatch(`/api/indian-exams/attempts/${encodeURIComponent(attemptId)}/cancel`, {
    status: 'cancelled',
    cancelledAt: new Date().toISOString()
  });
  return await res.json();
}, 'cancelAttempt', { attemptId });

/**
 * Submit/Complete an attempt
 * @param {string} attemptId - Attempt ID
 * @param {Object} submissionData - { answers, timeElapsed, score }
 * @returns {Promise<Object>}
 */
export const submitAttempt = async (attemptId, submissionData) => safeApiCall(async () => {
  const res = await httpPost(`/api/indian-exams/attempts/${encodeURIComponent(attemptId)}/complete`, {
    status: 'completed',
    completedAt: new Date().toISOString(),
    ...submissionData
  });
  return await res.json();
}, 'submitAttempt', { attemptId });

/**
 * Reset an attempt (for practice mode - start over)
 * @param {string} attemptId - Attempt ID
 * @returns {Promise<Object>}
 */
export const resetAttempt = async (attemptId) => safeApiCall(async () => {
  const res = await httpPost(`/api/indian-exams/attempts/${encodeURIComponent(attemptId)}/reset`, {
    status: 'in_progress',
    answers: {},
    currentQuestionIndex: 0,
    timeElapsed: 0,
    resetAt: new Date().toISOString()
  });
  return await res.json();
}, 'resetAttempt', { attemptId });

/**
 * Get all attempts for a user
 * @param {Object} filters - { examId, paperId, status, limit, page }
 * @returns {Promise<Object>}
 */
export const getUserAttempts = async (filters = {}) => safeApiCall(async () => {
  const params = new URLSearchParams();
  if (filters.examId) params.append('examId', filters.examId);
  if (filters.paperId) params.append('paperId', filters.paperId);
  if (filters.status) params.append('status', filters.status);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.page) params.append('page', filters.page);
  
  const queryString = params.toString() ? `?${params.toString()}` : '';
  const res = await httpGet(`/api/indian-exams/attempts${queryString}`);
  return await res.json();
}, 'getUserAttempts', { filters });

/**
 * Get attempt statistics
 * @param {string} attemptId - Attempt ID
 * @returns {Promise<Object>} - Statistics including score, accuracy, time per question, etc.
 */
export const getAttemptStatistics = async (attemptId) => safeApiCall(async () => {
  const res = await httpGet(`/api/indian-exams/attempts/${encodeURIComponent(attemptId)}/statistics`);
  return await res.json();
}, 'getAttemptStatistics', { attemptId });

/**
 * Mark question for review
 * @param {string} attemptId - Attempt ID
 * @param {string} questionId - Question ID
 * @param {boolean} marked - Mark or unmark for review
 * @returns {Promise<Object>}
 */
export const markForReview = async (attemptId, questionId, marked = true) => safeApiCall(async () => {
  const res = await httpPatch(`/api/indian-exams/attempts/${encodeURIComponent(attemptId)}/mark-review`, {
    questionId,
    markedForReview: marked,
    timestamp: new Date().toISOString()
  });
  return await res.json();
}, 'markForReview', { attemptId, questionId, marked });
